// Продуктовое включение и таргетинг модуля (ТЗ §6.3) — `admin_settings.modules`.
//
// Вынесено из `availability.ts` отдельным файлом по одной причине: резолвер
// импортирует `KILLSWITCH_OPS` из `contracts/admin`, а admin-контракт должен
// импортировать схему раскатки (её редактирует админ). Держи схему в
// `availability.ts` — получишь цикл `availability → contracts/admin →
// availability`. Этот файл зависит только от `zod` и `ids`/`constants`,
// поэтому его можно импортировать откуда угодно.

import { z } from "zod";
import { SUBSCRIPTION_TIERS } from "../constants.js";
import { moduleIdSchema } from "./ids.js";

/**
 * Запись `admin_settings.modules[<id>]`. Zod — потому что это НЕДОВЕРЕННЫЙ вход:
 * JSONB из БД, переживший несколько версий кода. Тип выводится из схемы
 * (`z.infer`), второго объявления нет.
 */
export const moduleRolloutSchema = z.object({
	state: z.enum(["on", "off"]),
	/** Ограничение по тарифам. Практически — `plus`/`pro`; `free` схема не запрещает. */
	tiers: z.array(z.enum(SUBSCRIPTION_TIERS)).optional(),
	/** Когорты бета-тестеров. */
	cohorts: z.array(z.string().min(1).max(64)).max(50).optional(),
	/** Процент раскатки 0..100 по детерминированному хэшу userId. */
	percentage: z.number().int().min(0).max(100).optional(),
});
export type ModuleRollout = z.infer<typeof moduleRolloutSchema>;

/**
 * Карта раскатки целиком — то, что лежит в `admin_settings.modules`.
 *
 * Частичная сознательно: запись заводится только на модули, которые админ
 * трогал руками. Требовать все 21 id значило бы, что первый же новый модуль
 * делает всю карту невалидной.
 */
export const moduleRolloutMapSchema = z.partialRecord(moduleIdSchema, moduleRolloutSchema);
export type ModuleRolloutMap = z.infer<typeof moduleRolloutMapSchema>;

/**
 * Терпимый разбор карты из JSONB.
 *
 * Строгий `parse` здесь недопустим: значение пережило несколько версий кода, и
 * один битый ключ (удалённый модуль, поле от старой схемы) не должен обнулять
 * раскатку всех остальных — это выглядело бы как «админ ничего не настраивал»
 * и молча включило бы выключенные модули.
 *
 * Поэтому запись за записью, а брак — НЕ молча: список проблем возвращается
 * вызывающему, который обязан крикнуть `logger.warn({ degradation })`
 * (`rules/logging.md` §13a). Тихо выброшенная запись = тумблер, который
 * перестал работать, не оставив следа.
 */
export function parseModuleRolloutMap(raw: unknown): {
	rollout: ModuleRolloutMap;
	issues: string[];
} {
	if (raw === null || raw === undefined) return { rollout: {}, issues: [] };
	if (typeof raw !== "object" || Array.isArray(raw)) {
		return { rollout: {}, issues: [`ожидался объект, получен ${typeof raw}`] };
	}
	const rollout: ModuleRolloutMap = {};
	const issues: string[] = [];
	for (const [key, value] of Object.entries(raw)) {
		const id = moduleIdSchema.safeParse(key);
		if (!id.success) {
			issues.push(`неизвестный модуль "${key}"`);
			continue;
		}
		const entry = moduleRolloutSchema.safeParse(value);
		if (!entry.success) {
			issues.push(`битая запись "${key}": ${entry.error.issues[0]?.message ?? "invalid"}`);
			continue;
		}
		rollout[id.data] = entry.data;
	}
	return { rollout, issues };
}

// ─── Хранение в `admin_settings` ────────────────────────────────────────────

/** Ключ `admin_settings`, под которым лежит раскатка модулей. */
export const MODULE_ROLLOUT_SETTING_KEY = "modules";

/**
 * Значение настройки целиком.
 *
 * `version` лежит ВНУТРИ той же записи, а не отдельным Redis-ключом: поколение
 * и карта обязаны меняться атомарно. Разнеси их — и клиент однажды получит
 * новую карту со старым поколением (или наоборот), то есть будет уверен, что
 * у него свежий снапшот, когда он устарел.
 */
export interface ModuleRolloutSetting {
	version: number;
	rollout: ModuleRolloutMap;
}

/**
 * Терпимый разбор значения настройки. Понимает две формы:
 *  - актуальную `{ version, rollout }`;
 *  - «голую» карту `{ notes: {...} }` — на случай, если запись когда-то была
 *    записана вручную или более ранней версией кода. Молча потерять раскатку
 *    из-за смены формы конверта нельзя.
 */
export function parseModuleRolloutSetting(raw: unknown): {
	setting: ModuleRolloutSetting;
	issues: string[];
} {
	if (raw && typeof raw === "object" && !Array.isArray(raw) && "rollout" in raw) {
		const envelope = raw as { version?: unknown; rollout?: unknown };
		const parsed = parseModuleRolloutMap(envelope.rollout);
		const version =
			typeof envelope.version === "number" && Number.isFinite(envelope.version)
				? Math.max(0, Math.trunc(envelope.version))
				: 0;
		return { setting: { version, rollout: parsed.rollout }, issues: parsed.issues };
	}
	const parsed = parseModuleRolloutMap(raw);
	return { setting: { version: 0, rollout: parsed.rollout }, issues: parsed.issues };
}
